import React, { useState } from "react";
import { useInterval, useTimeout, useUnmount } from "usehooks-ts";
import { Button } from "@picklebet/ui";
import { ReactComponent as SuccessArt } from "@/features/wallet/assets/success.svg";
import { convertTimeToUserTimeZone } from "@/utilities/datetime";
import { navigate } from "@/utilities/navigate";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import type { WithdrawCurrencyId } from "@/features/wallet/components/withdraw/types";
import * as styles from "./WithdrawSuccess.module.scss";

const AUTO_CLOSE_SECONDS = 10;

const AUD_PROCESSING_WINDOW = {
  timeZone: "Australia/Sydney",
  start: "8:00",
  end: "20:00",
} as const;

const IOM_CURRENCIES: readonly WithdrawCurrencyId[] = ["CAD", "NZD"];

const processingCopy = (currency: WithdrawCurrencyId) => {
  if (IOM_CURRENCIES.includes(currency)) {
    return "Withdrawals are processed multiple times per day, 7 days a week.";
  }

  const { timeZone, start, end } = AUD_PROCESSING_WINDOW;

  const window = `${convertTimeToUserTimeZone(
    start,
    timeZone,
  )} – ${convertTimeToUserTimeZone(end, timeZone)}`;

  return `Eligible Withdrawals are processed same-day between ${window}, Monday to Sunday. Please note: Your first withdrawal will be manually reviewed.`;
};

export const WithdrawSuccess = () => {
  const { close, currency } = useWithdrawWizard();
  const [secondsRemaining, setSecondsRemaining] = useState(AUTO_CLOSE_SECONDS);

  useTimeout(close, AUTO_CLOSE_SECONDS * 1000);

  // This guy has multiple exit points including a timeout, so this is the
  // easiest way to navigate after success has been dismissed
  useUnmount(() => {
    navigate("/wallet/processing");
  });

  useInterval(
    () => setSecondsRemaining((remaining) => remaining - 1),
    secondsRemaining > 0 ? 1000 : null,
  );

  return (
    <div className={styles.container}>
      <SuccessArt className={styles.art} />

      <div className={styles.title}>
        <h3>Withdrawal Requested</h3>
        <h5>{processingCopy(currency)}</h5>
      </div>

      <Button
        variant="secondary"
        size="sm"
        onClick={close}
        className={styles.done}
      >
        Done ({secondsRemaining}s)
      </Button>
    </div>
  );
};
